import { toast } from '@iq/ui';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { describeError, extractStatus } from './errors';

const NON_RETRYABLE_STATUSES = new Set([401, 403, 404, 422]);
/**
 * Statuses we route through the global toast/inline-error UX. 401 already has
 * dedicated routing (invalidate session + redirect) so we suppress its toast
 * here; mutations and explicit per-call handlers can still surface their own.
 * 404/422 are usually meaningful in-page (form validation, "not found"
 * placeholders) — same suppression.
 */
const SUPPRESSED_TOAST_STATUSES = new Set([401, 404, 422]);

export function createQueryClient(): QueryClient {
  const client: QueryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        retry: (failureCount, error) => {
          const status = extractStatus(error);
          if (status !== undefined && NON_RETRYABLE_STATUSES.has(status)) {
            return false;
          }
          return failureCount < 2;
        },
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        throwOnError: false,
      },
      mutations: {
        retry: false,
        throwOnError: false,
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        const status = extractStatus(error);
        if (status === 401) {
          // PR2 only invalidates the session query. Router-level navigation
          // away from authed routes lands with the router in PR3.
          client.invalidateQueries({ queryKey: ['auth', 'session'] });
        }
        // Surface unexpected errors as toasts so failed background fetches
        // don't sit silently in the DevTools console. Per-component code can
        // opt out by setting `meta: { suppressToast: true }` on its useQuery.
        const meta = query?.options?.meta as { suppressToast?: boolean } | undefined;
        if (meta?.suppressToast) return;
        if (status !== undefined && SUPPRESSED_TOAST_STATUSES.has(status)) return;
        toast.error(describeError(error));
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _vars, _ctx, mutation) => {
        // Same opt-out pattern as queries — mutations that own their own
        // inline error UX (sign-in form, password change form) set
        // `meta: { suppressToast: true }` to avoid double-display.
        const meta = mutation?.options?.meta as { suppressToast?: boolean } | undefined;
        if (meta?.suppressToast) return;
        const status = extractStatus(error);
        if (status !== undefined && SUPPRESSED_TOAST_STATUSES.has(status)) return;
        toast.error(describeError(error));
      },
    }),
  });
  return client;
}

/**
 * Singleton client used by `main.tsx`. Tests must NOT use this — they should
 * call `createQueryClient()` per test for isolation.
 */
export const queryClient = createQueryClient();
